import { Avatar, Card, Chip, Link } from "@heroui/react";

import { ModerationAction } from "@/src/components/ModerationAction";
import { PostStatusChip } from "@/src/components/PostStatusChip";
import { useCategoryName } from "@/src/hooks/useCategories";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { initials } from "@/src/libs/initials";
import { ScoreBadge } from "@/src/pages/Posts/components/ScoreBadge";
import { ScoreDetails } from "@/src/pages/Posts/components/ScoreDetails";
import { parseModerationScores } from "@/src/pages/Posts/moderationScores";
import type { ModerationPost } from "@/src/pages/Posts/posts.type";
import { ROUTES } from "@/src/routes/paths";

type PostCardProps = {
  post: ModerationPost;
  isPending: boolean;
  onApprove: (_id: number) => void;
  onReject: (_id: number) => void;
  onDelete: (_id: number) => void;
};

/**
 * Une publication dans la liste du fil.
 *
 * ## Ce qu'elle montre, et pourquoi dans cet ordre
 *
 * Les **photos d'abord** : c'est sur elles qu'on tranche, et le texte n'est
 * souvent qu'une légende. Puis l'auteur — cliquable vers sa fiche, parce qu'un
 * compte qui accumule les publications douteuses se juge autrement qu'un
 * premier écart. Puis les scores, qui expliquent pourquoi elle est là.
 *
 * ## Trois gestes, deux poids
 *
 * Valider et refuser sont réversibles — l'un défait l'autre. **Effacer ne l'est
 * pas** : cascades sur photos, cœurs et commentaires, plus les objets S3. Il est
 * donc en rouge et à part, comme le veut `ModerationAction`.
 *
 * ⚠️ **Pas de lien étiré sur toute la carte**, contrairement à `EventRow` : la
 * carte porte trois boutons, et une surface cliquable posée par-dessus leur
 * disputerait le clic. Le titre suffit à ouvrir la fiche, et la carte reste
 * autosuffisante — on tranche depuis la liste, la fiche est pour les cas qui
 * demandent le fil des commentaires.
 */
export function PostCard({
  post,
  isPending,
  onApprove,
  onReject,
  onDelete,
}: PostCardProps) {
  const t = useTranslations("posts");
  const ta = useTranslations("a11y");
  const { locale } = useLocale();

  // Le nom vient du serveur, jamais d'une table écrite ici : elle divergerait
  // dès le premier jeu ajouté. Tant qu'il n'est pas connu, pas de pastille —
  // un identifiant nu n'apprendrait rien à personne.
  const categorie = useCategoryName(post.categoryId);

  const scores = parseModerationScores(post.moderationScores);

  const publie = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(post.createdAt));

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Une publication n'a pas de titre : son numéro est le seul nom qu'on
            puisse lui donner, et c'est aussi celui que porte un signalement. */}
        <Link className="text-sm font-medium" href={ROUTES.post(post.id)}>
          {t("post-title", { id: post.id })}
        </Link>

        <PostStatusChip status={post.moderationStatus} />

        {/* ⚠️ **Retirée par son auteur n'est pas un statut**, et la pastille ne
            remplace donc pas celle du statut : une publication approuvée que
            son auteur a retirée reste `approved` — elle a simplement quitté le
            fil, sans que la modération l'ait décidé. */}
        {post.deletedAt ? (
          <Chip color="default" size="sm" variant="secondary">
            {t("deleted-by-author")}
          </Chip>
        ) : null}

        <Chip size="sm" variant="secondary">
          {post.type === "gallery" ? t("type-gallery") : t("type-thread")}
        </Chip>

        {categorie ? (
          <Chip size="sm" variant="secondary">
            {categorie}
          </Chip>
        ) : null}

        <ScoreBadge scores={scores} />

        <span className="ml-auto text-xs text-muted">{publie}</span>
      </div>

      {/* Les photos, avant le texte : c'est sur elles qu'on tranche. */}
      {post.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {post.photos.map((photo, rang) => (
            <img
              alt={ta("post-photo", { position: rang + 1 })}
              className="size-28 rounded-md object-cover"
              key={photo.id}
              loading="lazy"
              src={photo.url}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Avatar className="size-8 shrink-0" size="sm">
          {post.author.avatar ? (
            <Avatar.Image alt="" src={post.author.avatar} />
          ) : null}
          <Avatar.Fallback className="text-xs">
            {initials(post.author.username)}
          </Avatar.Fallback>
        </Avatar>

        {/*
          L'auteur mène à sa fiche : un compte qui accumule les publications
          douteuses ne se juge pas comme un premier écart.
        */}
        <Link
          className="text-sm font-medium"
          href={ROUTES.account(post.author.accountId)}
        >
          {post.author.username}
        </Link>

        {/* L'engagement se lit ici, et pas seulement sur la fiche : une
            publication que quatre cents personnes ont vue n'est pas le même
            dossier que celle que trois personnes ont vue. */}
        <span className="ml-auto text-xs text-muted tabular-nums">
          {t("engagement", {
            likes: post.likeCount,
            comments: post.commentCount,
          })}
        </span>
      </div>

      {post.content && (
        <p className="text-sm whitespace-pre-wrap">{post.content}</p>
      )}

      <ScoreDetails photos={post.photos} scores={scores} />

      <div className="flex flex-wrap items-center gap-2">
        <ModerationAction
          confirmBody={t("confirm-approve-body", {
            author: post.author.username,
          })}
          confirmLabel={t("approve")}
          confirmTitle={t("confirm-approve-title")}
          isPending={isPending}
          label={t("approve")}
          onConfirm={() => onApprove(post.id)}
          pendingLabel={t("approving")}
        />

        <ModerationAction
          confirmBody={t("confirm-reject-body", {
            author: post.author.username,
          })}
          confirmLabel={t("reject")}
          confirmTitle={t("confirm-reject-title")}
          isPending={isPending}
          label={t("reject")}
          onConfirm={() => onReject(post.id)}
          pendingLabel={t("rejecting")}
        />

        {/*
          À part, et en rouge : c'est le seul geste de cet écran qui détruise
          réellement quelque chose. Les deux autres se défont l'un l'autre.
        */}
        <div className="ml-auto">
          <ModerationAction
            confirmBody={t("confirm-delete-body", {
              author: post.author.username,
            })}
            confirmLabel={t("delete")}
            confirmTitle={t("confirm-delete-title")}
            isPending={isPending}
            label={t("delete")}
            onConfirm={() => onDelete(post.id)}
            pendingLabel={t("deleting")}
            tone="danger"
          />
        </div>
      </div>
    </Card>
  );
}
